import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { ApiError } from '../../api/client'
import {
  addMatrixLevel,
  priorityMatrixSettingsQuery,
  removeMatrixLevel,
  seedMatrixDefaults,
  setMatrixCell,
  setMatrixEnabled,
  updateMatrixLevel,
  type MatrixAxis,
  type MatrixLevel,
  type PriorityMatrixSettings,
} from '../../api/priorityMatrix'
import { toast } from '../../ui/notify'

/**
 * Settings → Easeedesk → Ticket priorities → MATRICEA URGENTA x IMPACT.
 *
 * Ecran nou (CEC L25), fara pereche in portalul vechi, asezat sub tabelul de
 * prioritati: matricea alege una din prioritatile de deasupra.
 *
 * Trei bucati, de sus in jos:
 *   1. comutatorul - stins inseamna comportamentul de azi, prioritatea aleasa
 *      de mana pe fiecare tichet;
 *   2. nivelurile - urgenta (High / Medium / Low) si impactul (Extensive /
 *      Significant / Moderate / Minor), editabile; la impact, pragul de lucruri
 *      atinse de la care fisa tichetului il SUGEREAZA;
 *   3. tabelul - pe fiecare pereche, prioritatea care iese.
 *
 * Fiecare schimbare se scrie pe loc, ca restul ecranului de prioritati.
 */
export function PriorityMatrixPanel() {
  const queryClient = useQueryClient()
  const page = useQuery(priorityMatrixSettingsQuery())

  const reload = () => {
    void queryClient.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'priority_matrix'] })
  }
  const failed = (error: unknown) =>
    toast(error instanceof ApiError && error.message ? error.message : 'Something went wrong.', 'Priority matrix', 'error')

  const toggle = useMutation({
    mutationFn: (on: boolean) => setMatrixEnabled(on),
    onSuccess: (_d, on) => {
      toast(on ? 'Priority matrix enabled' : 'Priority matrix disabled', 'Priority matrix')
      reload()
    },
    onError: failed,
  })
  const seed = useMutation({
    mutationFn: () => seedMatrixDefaults(),
    onSuccess: () => {
      toast('ITIL levels added', 'Priority matrix')
      reload()
    },
    onError: failed,
  })
  const cell = useMutation({
    mutationFn: (v: { urgency: number; impact: number; priority: number }) => setMatrixCell(v.urgency, v.impact, v.priority),
    onSuccess: reload,
    onError: (error) => {
      failed(error)
      reload()
    },
  })

  /* Ecranul n-are inca placa (404) sau omul n-are dreptul: tabelul de
     prioritati de deasupra ramane, matricea nu se arata. */
  if (page.error) {
    if (page.error instanceof ApiError && (page.error.status === 404 || page.error.status === 403)) return null
    return (
      <div className="alert alert-warning priority-matrix-error">
        The priority matrix could not be loaded: {page.error.message}
      </div>
    )
  }
  /* Fara rotita: ecranul are deja una pentru lista de prioritati, si o a doua
     ar parea ca lista inca se incarca. */
  if (!page.data) return null

  const data = page.data

  return (
    <section className="priority-matrix" aria-labelledby="priority-matrix-title">
      <hr />
      <h4 id="priority-matrix-title">
        <strong>Priority</strong> matrix (urgency x impact)
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        When enabled, a ticket&apos;s priority is calculated from its <strong>urgency</strong> and <strong>impact</strong>.
        Disabled, priorities are chosen by hand, as before.
      </label>

      {!data.ready && (
        <div className="alert alert-warning">This needs a database update that has not been applied yet.</div>
      )}

      <div className="checkbox">
        <label>
          <input
            type="checkbox"
            name="priority_matrix_enabled"
            checked={data.enabled}
            disabled={!data.ready || toggle.isPending}
            onChange={(e) => toggle.mutate(e.target.checked)}
          />{' '}
          Calculate ticket priority from the matrix
        </label>
      </div>

      {data.ready && data.urgencies.length === 0 && data.impacts.length === 0 && (
        <p>
          <button type="button" className="btn btn-default btn-sm" disabled={seed.isPending} onClick={() => seed.mutate()}>
            <i className="fas fa-magic" aria-hidden="true"></i> Start from the ITIL levels
          </button>{' '}
          <small className="text-muted">High / Medium / Low urgency, Extensive / Significant / Moderate / Minor impact.</small>
        </p>
      )}

      {data.ready && (
        <div className="row">
          <div className="col-md-6">
            <LevelList axis="urgency" title="Urgency levels" levels={data.urgencies} onChanged={reload} onFailed={failed} />
          </div>
          <div className="col-md-6">
            <LevelList axis="impact" title="Impact levels" levels={data.impacts} onChanged={reload} onFailed={failed} />
          </div>
        </div>
      )}

      {data.urgencies.length > 0 && data.impacts.length > 0 && (
        <MatrixGrid data={data} busy={cell.isPending} onCell={(urgency, impact, priority) => cell.mutate({ urgency, impact, priority })} />
      )}
    </section>
  )
}

function MatrixGrid({
  data,
  busy,
  onCell,
}: {
  data: PriorityMatrixSettings
  busy: boolean
  onCell(urgency: number, impact: number, priority: number): void
}) {
  const valueOf = (u: number, i: number) => data.cells.find((c) => c.urgency_id === u && c.impact_id === i)?.priority_id ?? 0

  return (
    <table className="table table-bordered priority-matrix-grid">
      <caption>Resulting priority</caption>
      <thead>
        <tr>
          <th scope="col">Urgency \ Impact</th>
          {data.impacts.map((impact) => (
            <th scope="col" key={impact.ID}>
              {impact.name}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {data.urgencies.map((urgency) => (
          <tr key={urgency.ID}>
            <th scope="row">{urgency.name}</th>
            {data.impacts.map((impact) => (
              <td key={impact.ID}>
                <select
                  className="form-control input-sm"
                  aria-label={`Priority for ${urgency.name} urgency and ${impact.name} impact`}
                  value={valueOf(urgency.ID, impact.ID)}
                  disabled={busy}
                  onChange={(e) => onCell(urgency.ID, impact.ID, Number(e.target.value))}
                >
                  <option value={0}>- not set -</option>
                  {data.priorities.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function LevelList({
  axis,
  title,
  levels,
  onChanged,
  onFailed,
}: {
  axis: MatrixAxis
  title: string
  levels: MatrixLevel[]
  onChanged(): void
  onFailed(error: unknown): void
}) {
  const [name, setName] = useState('')
  const add = useMutation({
    mutationFn: () => addMatrixLevel(axis, name.trim(), levels.length + 1, null),
    onSuccess: () => {
      setName('')
      onChanged()
    },
    onError: onFailed,
  })

  return (
    <div className={`priority-matrix-levels priority-matrix-${axis}`}>
      <h5>
        <strong>{title}</strong>
      </h5>
      <ul className="list-unstyled">
        {levels.map((level) => (
          <LevelRow key={level.ID} axis={axis} level={level} onChanged={onChanged} onFailed={onFailed} />
        ))}
      </ul>
      <form
        className="form-inline"
        onSubmit={(e) => {
          e.preventDefault()
          if (name.trim() !== '') add.mutate()
        }}
      >
        <input
          type="text"
          className="form-control input-sm"
          aria-label={`New ${axis} level`}
          placeholder={`New ${axis} level`}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />{' '}
        <button type="submit" className="btn btn-success btn-sm" disabled={name.trim() === '' || add.isPending}>
          <i className="fas fa-plus" aria-hidden="true"></i> Add
        </button>
      </form>
    </div>
  )
}

function LevelRow({
  axis,
  level,
  onChanged,
  onFailed,
}: {
  axis: MatrixAxis
  level: MatrixLevel
  onChanged(): void
  onFailed(error: unknown): void
}) {
  const [name, setName] = useState(level.name)
  const [order, setOrder] = useState(String(level.sort_order ?? 0))
  const [min, setMin] = useState(level.min_affected == null ? '' : String(level.min_affected))

  useEffect(() => {
    setName(level.name)
    setOrder(String(level.sort_order ?? 0))
    setMin(level.min_affected == null ? '' : String(level.min_affected))
  }, [level.name, level.sort_order, level.min_affected])

  const save = useMutation({
    mutationFn: () => updateMatrixLevel(level.ID, name.trim(), Number(order) || 0, min === '' ? null : Number(min) || null),
    onSuccess: onChanged,
    onError: onFailed,
  })
  const remove = useMutation({
    mutationFn: () => removeMatrixLevel(level.ID),
    onSuccess: onChanged,
    onError: onFailed,
  })

  const dirty =
    name.trim() !== level.name ||
    order !== String(level.sort_order ?? 0) ||
    min !== (level.min_affected == null ? '' : String(level.min_affected))

  return (
    <li className="form-inline priority-matrix-level" style={{ marginBottom: 6 }}>
      <input
        type="number"
        min={0}
        className="form-control input-sm"
        style={{ width: 64 }}
        aria-label={`Order of ${level.name}`}
        title="Order (1 = most severe)"
        value={order}
        onChange={(e) => setOrder(e.target.value)}
      />{' '}
      <input
        type="text"
        className="form-control input-sm"
        aria-label={`Name of ${level.name}`}
        value={name}
        onChange={(e) => setName(e.target.value)}
      />{' '}
      {axis === 'impact' && (
        <>
          <input
            type="number"
            min={1}
            className="form-control input-sm"
            style={{ width: 90 }}
            aria-label={`Suggest ${level.name} from affected items`}
            title="Suggest this impact from this many affected items"
            placeholder="from #"
            value={min}
            onChange={(e) => setMin(e.target.value)}
          />{' '}
        </>
      )}
      <button
        type="button"
        className="btn btn-primary btn-xs"
        disabled={!dirty || name.trim() === '' || save.isPending}
        onClick={() => save.mutate()}
      >
        Save
      </button>{' '}
      <button
        type="button"
        className="btn btn-danger btn-xs"
        aria-label={`Delete ${level.name}`}
        disabled={remove.isPending}
        onClick={() => {
          if (!window.confirm(`Delete "${level.name}"?\nIts cells in the matrix are cleared.`)) return
          remove.mutate()
        }}
      >
        <i className="fas fa-trash-alt" aria-hidden="true"></i>
      </button>
    </li>
  )
}
